import { useInfiniteQuery } from '@tanstack/react-query'
import { useAuth } from '@clerk/clerk-react'
import { getDocuments } from '../api'
import type { DocumentType } from '../types'

export interface CollectionQueryParams {
  limit?: number
  sort_by?: 'updated_at' | 'created_at'
  sort_order?: 'asc' | 'desc'
  search?: string
  archived?: boolean
}

export interface DocumentRow {
  id: string
  name?: string | null
  description?: string | null
  created_at?: string | null
  updated_at?: string | null
  access?: string[] | null
  metadata?: { type?: DocumentType } | null
}

export interface CollectionResponse<T> {
  data: T[]
  pagination: {
    limit: number
    has_more: boolean
    next_cursor: string | null
  }
}

async function fetchDocuments(
  token: string,
  params: CollectionQueryParams,
  cursor?: string
) {
  const qs = new URLSearchParams()
  if (params.limit) qs.set('limit', params.limit.toString())
  if (params.sort_by) qs.set('sort_by', params.sort_by)
  if (params.sort_order) qs.set('sort_order', params.sort_order)
  if (params.search) qs.set('search', params.search)
  if (params.archived) qs.set('archived', 'true')
  if (cursor) qs.set('cursor', cursor)

  return getDocuments(token, qs)
}

export function useDocumentTableQuery(params: CollectionQueryParams = {}) {
  const { getToken } = useAuth()

  return useInfiniteQuery<CollectionResponse<DocumentRow>>({
    queryKey: ['documents', params],
    queryFn: async ({ pageParam }) => {
      const token = await getToken()
      if (!token) {
        throw new Error(`Missing auth token`)
      }
      return fetchDocuments(token, params, pageParam as string | undefined)
    },
    getNextPageParam: (lastPage) =>
      lastPage.pagination.next_cursor ?? undefined,
    initialPageParam: undefined,
  })
}
